import { Middleware, AnyAction } from '@reduxjs/toolkit'
import { updateUrlWithFilters } from '@/utils/urlSync'
import type { RootState } from '@/store/slices/reducers'

/**
 * Middleware to sync Redux filter changes to URL parameters
 */
export const urlSyncMiddleware: Middleware<{}, RootState> =
  (store) => (next) => {
    let lastLandFilters: any = null
    let lastBuildingFilters: any = null
    let debounceTimeout: NodeJS.Timeout | null = null

    return (action: AnyAction) => {
      const result = next(action)

      // Skip if action is from URL sync to prevent infinite loops
      if (action.type.endsWith('setFiltersFromUrl')) {
        return result
      }

      // Only sync for filter-related actions
      const isFilterAction =
        action.type.includes('Filter') ||
        action.type.includes('landsFilter') ||
        action.type.includes('buildingsFilter')

      if (!isFilterAction) {
        return result
      }

      const state = store.getState()
      const currentLandFilters = state.landsFilter.filters
      const currentBuildingFilters = state.buildingsFilter.filters

      // Check if filters actually changed
      const landFiltersChanged =
        JSON.stringify(currentLandFilters) !== JSON.stringify(lastLandFilters)
      const buildingFiltersChanged =
        JSON.stringify(currentBuildingFilters) !==
        JSON.stringify(lastBuildingFilters)

      if (landFiltersChanged || buildingFiltersChanged) {
        // Clear existing debounce
        if (debounceTimeout) {
          clearTimeout(debounceTimeout)
        }

        // Debounce URL updates to prevent excessive history entries
        debounceTimeout = setTimeout(() => {
          try {
            // Determine which filters to sync based on current URL mode
            const urlParams = new URLSearchParams(window.location.search)
            const currentType = urlParams.get('type')
            const currentMode = urlParams.get('mode') || 'plot'

            if (
              landFiltersChanged &&
              (currentMode === 'plot' || currentType === 'land' || !currentType)
            ) {
              updateUrlWithFilters(currentLandFilters, 'lands', true) // Use replace to avoid history spam
              lastLandFilters = currentLandFilters
            }

            if (
              buildingFiltersChanged &&
              (currentMode === 'building' || currentType === 'building')
            ) {
              updateUrlWithFilters(currentBuildingFilters, 'buildings', true)
              lastBuildingFilters = currentBuildingFilters
            }
          } catch (error) {
            console.warn('URL sync middleware error:', error)
          }
        }, 300) // 300ms debounce
      }

      return result
    }
  }
